import React, { useEffect, useState } from "react";
import axios from "../../services/api";
import ReactPaginate from "react-paginate";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { format } from "date-fns";

/* ── GRN status badge ── */
const grnStatusBadge = (status) => {
  const map = {
    "Partial":  "ims-badge-partial",
    "Full":     "ims-badge-full",
    "Received": "ims-badge-approved",
    "Pending":  "ims-badge-pending",
  };
  return (
    <span className={`ims-badge ${map[status] || "ims-badge-notcreated"}`}>
      {status}
    </span>
  );
};

const GrnList = () => {
  const [grns, setGrns] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);

  const user = JSON.parse(localStorage.getItem("user"));
  const token = localStorage.getItem("token");

  const fetchGrns = async () => {
    try {
      const response = await axios.get(`/grns`, {
        params: {
          page: currentPage + 1,
          limit: itemsPerPage,
          search: searchTerm,
          user_id: user.id,
          role: user.role,
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      setGrns(response.data.data || []);
      setTotalPages(response.data.totalPages || 0);
    } catch (error) {
      console.error("Error fetching GRNs:", error);
    }
  };

  useEffect(() => {
    fetchGrns();
  }, [currentPage, searchTerm, itemsPerPage]);

  const handlePageClick = ({ selected }) => setCurrentPage(selected);

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(grns);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "GRNs");
    XLSX.writeFile(workbook, "GRNs.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Goods Received Notes", 14, 15);
    const tableData = grns.map((grn, i) => [
      i + 1,
      grn.po_number,
      format(new Date(grn.po_date), "dd-MM-yyyy"),
      grn.vendor_name,
      grn.grn_number,
      format(new Date(grn.grn_date), "dd-MM-yyyy"),
      grn.status,
    ]);
    autoTable(doc, {
      startY: 20,
      head: [["Sr", "PO No.", "PO Date", "Vendor", "GRN No.", "GRN Date", "Status"]],
      body: tableData,
      theme: "grid",
    });
    doc.save("GRNs.pdf");
  };

  const generateGrnPDF = async (id) => {
    try {
      const response = await axios.get(`/grns/${id}/pdf`, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      window.open(URL.createObjectURL(blob), "_blank");
    } catch (error) {
      console.error("Error opening GRN PDF:", error);
      alert("Failed to open GRN PDF");
    }
  };

  return (
    <div>
      {/* ── Page header ── */}
      <div className="ims-page-header">
        <div>
          <h4>Goods Received Notes</h4>
          <p>Track all GRNs linked to purchase orders</p>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-sm btn-success" onClick={exportToExcel}>
            <i className="fas fa-file-excel mr-1"></i> Excel
          </button>
          <button className="btn btn-sm btn-danger" onClick={exportToPDF}>
            <i className="fas fa-file-pdf mr-1"></i> PDF
          </button>
        </div>
      </div>

      {/* ── Controls ── */}
      <div className="row mb-3 align-items-center">
        <div className="col-md-4 d-flex align-items-center">
          <label className="mr-2 mb-0" style={{ whiteSpace: "nowrap", fontSize: "13px" }}>Show:</label>
          <select
            className="form-control form-control-sm w-auto"
            value={itemsPerPage}
            onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(0); }}
          >
            {[10, 20, 50, 100].map((num) => (
              <option key={num}>{num}</option>
            ))}
          </select>
        </div>
        <div className="col-md-4">
          <input
            type="text"
            placeholder="Search GRNs..."
            className="form-control form-control-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* ── Table ── */}
      <div className="table-responsive">
        <table className="ims-table">
          <thead>
            <tr>
              <th>Sr</th>
              <th>PO No.</th>
              <th>PO Date</th>
              <th>Vendor</th>
              <th>GRN No.</th>
              <th>GRN Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {grns.length > 0 ? (
              grns.map((grn, i) => (
                <tr key={grn.grn_id}>
                  <td>{i + 1 + currentPage * itemsPerPage}</td>
                  <td><strong>{grn.po_number}</strong></td>
                  <td>{format(new Date(grn.po_date), "dd-MM-yyyy")}</td>
                  <td>{grn.vendor_name}</td>
                  <td><strong>{grn.grn_number}</strong></td>
                  <td>{format(new Date(grn.grn_date), "dd-MM-yyyy")}</td>
                  <td>{grnStatusBadge(grn.status)}</td>
                  <td>
                    <button
                      className="ims-btn ims-btn-pdf"
                      title="Print GRN PDF"
                      onClick={() => generateGrnPDF(grn.grn_id)}
                    >
                      <i className="fas fa-print"></i>
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center text-muted py-4">
                  No GRNs found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ── */}
      <ReactPaginate
        previousLabel={"← Previous"}
        nextLabel={"Next →"}
        breakLabel={"..."}
        pageCount={totalPages}
        marginPagesDisplayed={2}
        pageRangeDisplayed={5}
        onPageChange={handlePageClick}
        containerClassName={"pagination justify-content-center mt-3"}
        pageClassName={"page-item"}
        pageLinkClassName={"page-link"}
        previousClassName={"page-item"}
        previousLinkClassName={"page-link"}
        nextClassName={"page-item"}
        nextLinkClassName={"page-link"}
        breakClassName={"page-item"}
        breakLinkClassName={"page-link"}
        activeClassName={"active"}
      />
    </div>
  );
};

export default GrnList;
